import toast from "react-hot-toast";
import { useEffect, useState, useCallback } from "react";
import api from "../services/api";
import { useNavigate } from "react-router-dom";

function Dashboard() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  const [title, setTitle] = useState("");
  const [priority, setPriority] = useState("Medium");
  const [dueDate, setDueDate] = useState("");

  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editPriority, setEditPriority] = useState("Medium");

  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [darkMode, setDarkMode] = useState(
    localStorage.getItem("theme") === "dark"
  );

  const navigate = useNavigate();
  const today = new Date().toISOString().slice(0, 10);

  /* ================= FETCH TASKS ================= */
  const fetchTasks = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get("/tasks");
      setTasks(res.data.tasks || []);
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Session expired");
        localStorage.removeItem("token");
        navigate("/");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  /* ================= ADD TASK ================= */
  const addTask = async () => {
    if (!title.trim()) return;

    await api.post("/tasks", { title, priority, dueDate });
    toast.success("Task added");

    setTitle("");
    setPriority("Medium");
    setDueDate("");
    fetchTasks();
  };

  /* ================= DELETE TASK ================= */
  const deleteTask = async (id) => {
    if (!window.confirm("Delete this task?")) return;

    await api.delete(`/tasks/${id}`);
    toast.success("Task deleted");
    fetchTasks();
  };

  /* ================= TOGGLE COMPLETE ================= */
  const toggleComplete = async (task) => {
    await api.put(`/tasks/${task._id}`, {
      completed: !task.completed,
    });
    fetchTasks();
  };

  /* ================= SAVE EDIT ================= */
  const saveEdit = async (id) => {
    await api.put(`/tasks/${id}`, {
      title: editTitle,
      priority: editPriority,
    });

    toast.success("Task updated");
    setEditingId(null);
    setEditTitle("");
    setEditPriority("Medium");
    fetchTasks();
  };

  /* ================= LOGOUT ================= */
  const logout = () => {
    localStorage.removeItem("token");
    toast.success("Logged out");
    navigate("/");
  };

  return (
    <div
      className={`min-h-screen p-10 ${
        darkMode ? "bg-gray-900 text-white" : "bg-gray-100 text-black"
      }`}
    >
      <div
        className={`max-w-xl mx-auto p-6 rounded shadow ${
          darkMode ? "bg-gray-800" : "bg-white"
        }`}
      >
        {/* HEADER */}
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold">My Tasks</h2>
          <button
            onClick={() => {
              const mode = !darkMode;
              setDarkMode(mode);
              localStorage.setItem("theme", mode ? "dark" : "light");
            }}
            className="text-sm"
          >
            {darkMode ? "Light" : "Dark"}
          </button>
        </div>

        {/* FILTERS */}
        <div className="flex gap-2 mb-4">
          {["all", "completed", "pending", "overdue"].map((type) => (
            <button
              key={type}
              onClick={() => setFilter(type)}
              className={`px-3 py-1 rounded text-sm ${
                filter === type
                  ? "bg-blue-500 text-white"
                  : "bg-gray-200 text-black"
              }`}
            >
              {type.charAt(0).toUpperCase() + type.slice(1)}
            </button>
          ))}
        </div>

        {/* SEARCH */}
        <input
          type="text"
          placeholder="Search tasks..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full border px-3 py-2 rounded mb-4 text-black"
        />

        {/* ADD TASK */}
        <div className="flex gap-2 mb-4">
          <input
            className="flex-1 border px-3 py-2 rounded text-black"
            placeholder="New task"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />

          <select
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
            className="border px-2 py-2 rounded text-black"
          >
            <option>High</option>
            <option>Medium</option>
            <option>Low</option>
          </select>

          <input
            type="date"
            className="border px-2 py-2 rounded text-black"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
          />

          <button
            onClick={addTask}
            className="bg-blue-500 text-white px-4 rounded"
          >
            Add
          </button>
        </div>

        {/* TASK LIST */}
        {loading ? (
          <p className="text-center text-gray-400">Loading tasks...</p>
        ) : (
          <ul>
            {tasks
              .filter((task) => {
                const match = task.title
                  .toLowerCase()
                  .includes(search.toLowerCase());

                if (!match) return false;
                if (filter === "completed") return task.completed;
                if (filter === "pending") return !task.completed;
                if (filter === "overdue")
                  return task.dueDate && task.dueDate.slice(0, 10) < today;

                return true;
              })
              .map((task) => (
                <li
                  key={task._id}
                  className="flex justify-between items-center border-b py-2"
                >
                  {editingId === task._id ? (
                    <div className="flex gap-2 flex-1">
                      <input
                        className="border px-2 py-1 rounded text-black"
                        value={editTitle}
                        onChange={(e) => setEditTitle(e.target.value)}
                      />

                      <select
                        value={editPriority}
                        onChange={(e) =>
                          setEditPriority(e.target.value)
                        }
                        className="border px-2 py-1 rounded text-black"
                      >
                        <option>High</option>
                        <option>Medium</option>
                        <option>Low</option>
                      </select>

                      <button onClick={() => saveEdit(task._id)}>Save</button>
                    </div>
                  ) : (
                    <>
                      <span
                        className={`flex-1 ${
                          task.completed
                            ? "line-through text-gray-400"
                            : task.dueDate &&
                              task.dueDate.slice(0, 10) < today
                            ? "text-red-500 font-semibold"
                            : ""
                        }`}
                      >
                        {task.title}
                        <span
                          className={`ml-2 px-2 py-1 text-xs rounded ${
                            task.priority === "High"
                              ? "bg-red-500 text-white"
                              : task.priority === "Medium"
                              ? "bg-yellow-400 text-black"
                              : "bg-green-500 text-white"
                          }`}
                        >
                          {task.priority}
                        </span>
                        {task.dueDate && (
                          <span className="ml-2 text-sm text-gray-500">
                            (Due: {task.dueDate.slice(0, 10)})
                          </span>
                        )}
                      </span>

                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setEditingId(task._id);
                            setEditTitle(task.title);
                            setEditPriority(task.priority);
                          }}
                        >
                          Edit
                        </button>
                        <button onClick={() => toggleComplete(task)}>
                          {task.completed ? "Undo" : "Done"}
                        </button>
                        <button onClick={() => deleteTask(task._id)}>
                          Delete
                        </button>
                      </div>
                    </>
                  )}
                </li>
              ))}
          </ul>
        )}

        <button
          onClick={logout}
          className="mt-6 text-red-400 underline"
        >
          Logout
        </button>
      </div>
    </div>
  );
}

export default Dashboard;
